import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'

import { DropzoneBlockedDefaultProps, dropzoneBlockedDefaultProps } from '../../constants/dropzoneBlockedDefaultProps'
import { DropzoneContent } from './elements/DropzoneContent'
import { useTranslateRejectedFilesErrors } from './hooks/useTranslateRejectedFilesErrors'
import * as Styled from './styles'
import { AcceptOption, resolveAcceptedTypes } from './utils/resolveAcceptedTypes'

export interface DropzoneProps extends Omit<DropzoneOptions, 'accept'> {
  accept?: AcceptOption
  blockedText?: ReactNode
  children?: ReactNode
  contentText?: ReactNode
  className?: string
  customTrigger?: ReactNode
  footerText?: ReactNode
  isBlocked?: boolean
  isUploading?: boolean
  onClick?: () => void
  onDragActive?: (isActive: boolean) => void
  raw?: boolean
  uploadingText?: ReactNode
}

export const Dropzone = ({
  accept,
  blockedText,
  children,
  className,
  contentText,
  customTrigger,
  disabled,
  footerText,
  isBlocked,
  isUploading,
  onClick,
  onDragActive,
  raw,
  uploadingText,
  onDrop,
  onDropRejected,
  maxFiles,
  maxSize,
  minSize,
  ...rest
}: DropzoneProps): ReactElement => {
  const acceptedTypes = resolveAcceptedTypes(accept)
  const { handleDrop, handleDropRejected } = useTranslateRejectedFilesErrors({
    onDrop,
    onDropRejected,
    maxFiles,
    maxSize,
    minSize,
    accept: acceptedTypes,
  })

  const dropzoneBlockedProps: DropzoneBlockedDefaultProps | undefined = isBlocked
    ? dropzoneBlockedDefaultProps
    : undefined

  const dropzoneOptions: DropzoneOptions = {
    ...rest,
    accept: acceptedTypes,
    disabled,
    maxFiles,
    maxSize,
    minSize,
    noClick: !!customTrigger,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    ...dropzoneBlockedProps,
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneOptions)

  useEffect(() => {
    onDragActive?.(isDragActive)
  }, [onDragActive, isDragActive])

  const handleDropzoneClick = useCallback(() => {
    if (!isBlocked && !customTrigger && !disabled) {
      open()
    }

    onClick?.()
  }, [isBlocked, customTrigger, disabled, onClick, open])

  return (
    <Styled.DropzoneWrapper
      blocked={isBlocked}
      className={className}
      disabled={disabled}
      clickDisabled={!!customTrigger}
      raw={raw}
      active={isDragActive}
      {...getRootProps()}
      onClick={handleDropzoneClick}
    >
      <input {...getInputProps()} />

      {children || (
        <DropzoneContent
          blockedText={blockedText}
          contentText={contentText}
          customFooterElement={customTrigger}
          disabled={disabled}
          footerText={footerText}
          isBlocked={isBlocked}
          isDragActive={isDragActive}
          isUploading={isUploading}
          uploadingText={uploadingText}
        />
      )}
    </Styled.DropzoneWrapper>
  )
}
